import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { ArrowLeft, Save, Plus, X, Dices, Pencil, Check } from 'lucide-react'
import { useSorteo, useCrearSorteo, useActualizarSorteo, useLaboratoriosSorteos } from '../hooks/useSorteos'
import type { SorteoCreatePayload, MensajePremio } from '../types/sorteos.types'
import { PremiosFormCard } from '../components/PremiosFormCard'

export function SorteoFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id

  const { data: sorteo, isLoading } = useSorteo(id ?? '')
  const crear = useCrearSorteo()
  const actualizar = useActualizarSorteo()
  const { data: laboratorios = [], isLoading: isLoadingLabs } = useLaboratoriosSorteos()

  const [form, setForm] = useState<SorteoCreatePayload>({
    nombre: '',
    descripcion: '',
    imagen: null,
    campos_excel: [],
    tipo_ruleta: 'slot',
    opciones_fijas: [],
    mostrar_filtro_pais: false,
    mensajes_premios: [],
    max_ganadores: 2,
    activo: true,
    laboratorio_id: null,
  })

  const [camposExcelStr, setCamposExcelStr] = useState('')
  const [nuevaOpcion, setNuevaOpcion] = useState('')
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (sorteo && isEditing && !initialized) {
      setForm({
        nombre: sorteo.nombre,
        descripcion: sorteo.descripcion,
        imagen: sorteo.imagen,
        campos_excel: sorteo.campos_excel,
        tipo_ruleta: sorteo.tipo_ruleta,
        opciones_fijas: sorteo.opciones_fijas,
        mostrar_filtro_pais: sorteo.mostrar_filtro_pais,
        mensajes_premios: sorteo.mensajes_premios,
        max_ganadores: sorteo.max_ganadores,
        activo: sorteo.activo,
        laboratorio_id: sorteo.laboratorio_id,
      })
      setCamposExcelStr(sorteo.campos_excel.join(', '))
      setInitialized(true)
    }
  }, [sorteo, isEditing, initialized])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Auto-agregar premio pendiente si hay datos en los campos
    const nombresPendientes = premioNombres.map(n => n.trim()).filter(Boolean)
    let premiosFinales = form.mensajes_premios ?? []
    if (nombresPendientes.length > 0) {
      const premios = premiosFinales
      const pos = parseInt(premioGanador) || (premios.length > 0 ? Math.max(...premios.map(p => p.posicion)) + 1 : 1)
      const mensaje = premioMensaje.trim()
      const imagen = premioImagen.trim()
      const nuevos: MensajePremio[] = nombresPendientes.map(nombre => ({
        posicion: pos,
        mensaje: nombre,
        mensaje_ganador: mensaje || undefined,
        imagen_url: imagen || undefined,
      }))
      premiosFinales = [...premios, ...nuevos]
    }

    const payload = {
      ...form,
      mensajes_premios: premiosFinales,
      campos_excel: camposExcelStr
        .split(',')
        .map(s => s.trim())
        .filter(Boolean),
    }

    if (isEditing && id) {
      await actualizar.mutateAsync({ id, payload })
      navigate('/sorteos')
    } else {
      const result = await crear.mutateAsync(payload)
      window.open(`/sorteos/${result.id}/vista`, '_blank')
      navigate('/sorteos')
    }
  }

  const addOpcion = () => {
    if (nuevaOpcion.trim()) {
      setForm(prev => ({
        ...prev,
        opciones_fijas: [...(prev.opciones_fijas ?? []), nuevaOpcion.trim()],
      }))
      setNuevaOpcion('')
    }
  }

  const removeOpcion = (index: number) => {
    setForm(prev => ({
      ...prev,
      opciones_fijas: (prev.opciones_fijas ?? []).filter((_, i) => i !== index),
    }))
  }

  // Estado temporal para agregar premios
  const [premioNombres, setPremioNombres] = useState<string[]>([''])
  const [premioGanador, setPremioGanador] = useState('')
  const [premioMensaje, setPremioMensaje] = useState('')
  const [premioImagen, setPremioImagen] = useState('')

  const addPremio = () => {
    const nombres = premioNombres.map(n => n.trim()).filter(Boolean)
    if (nombres.length === 0) return
    const premios = form.mensajes_premios ?? []
    const pos = parseInt(premioGanador) || (premios.length > 0 ? Math.max(...premios.map(p => p.posicion)) + 1 : 1)
    const mensaje = premioMensaje.trim()

    const imagen = premioImagen.trim()
    const nuevos: MensajePremio[] = nombres.map(nombre => ({
      posicion: pos,
      mensaje: nombre,
      mensaje_ganador: mensaje || undefined,
      imagen_url: imagen || undefined,
    }))

    setForm(prev => ({
      ...prev,
      mensajes_premios: [...premios, ...nuevos],
    }))
    setPremioNombres([''])
    setPremioGanador('')
    setPremioMensaje('')
    setPremioImagen('')
  }

  const addNombreField = () => {
    setPremioNombres(prev => [...prev, ''])
  }

  const updateNombre = (index: number, value: string) => {
    setPremioNombres(prev => prev.map((n, i) => i === index ? value : n))
  }

  const removeNombreField = (index: number) => {
    setPremioNombres(prev => prev.filter((_, i) => i !== index))
  }

  const removePremio = (index: number) => {
    setForm(prev => ({
      ...prev,
      mensajes_premios: (prev.mensajes_premios ?? []).filter((_, i) => i !== index),
    }))
  }

  const clearPremios = () => {
    setForm(prev => ({ ...prev, mensajes_premios: [] }))
  }

  // Estado para edición inline de premios
  const [editingPremioIndex, setEditingPremioIndex] = useState<number | null>(null)
  const [editingPremio, setEditingPremio] = useState<MensajePremio>({ posicion: 1, mensaje: '' })

  const startEditPremio = (index: number) => {
    const premio = (form.mensajes_premios ?? [])[index]
    if (premio) {
      setEditingPremio({ ...premio })
      setEditingPremioIndex(index)
    }
  }

  const saveEditPremio = () => {
    if (editingPremioIndex === null || !editingPremio.mensaje.trim()) return
    setForm(prev => ({
      ...prev,
      mensajes_premios: (prev.mensajes_premios ?? []).map((p, i) =>
        i === editingPremioIndex ? { ...editingPremio, mensaje: editingPremio.mensaje.trim() } : p
      ),
    }))
    setEditingPremioIndex(null)
  }

  const cancelEditPremio = () => {
    setEditingPremioIndex(null)
  }

  if (isEditing && (isLoading || isLoadingLabs)) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/sorteos')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <Dices className="h-5 w-5 text-teal-500" />
          <h1 className="text-2xl font-bold">{isEditing ? 'Editar Sorteo' : 'Crear Sorteo'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Básico */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-base">Información básica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre del sorteo *</Label>
              <Input
                id="nombre"
                value={form.nombre}
                onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Ej: Sorteo Navideño 2025"
                required
              />
            </div>
            <div>
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={form.descripcion}
                onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Descripción del sorteo"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="imagen">URL de imagen (opcional)</Label>
              <Input
                id="imagen"
                value={form.imagen ?? ''}
                onChange={(e) => setForm(prev => ({ ...prev, imagen: e.target.value || null }))}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label htmlFor="laboratorio">Laboratorio</Label>
              <Select
                value={form.laboratorio_id ? String(form.laboratorio_id) : ''}
                onValueChange={(v) => setForm(prev => ({ ...prev, laboratorio_id: v ? Number(v) : null }))}
              >
                <SelectTrigger id="laboratorio">
                  <SelectValue placeholder="Seleccionar laboratorio..." />
                </SelectTrigger>
                <SelectContent>
                  {laboratorios.map((lab) => (
                    <SelectItem key={lab.id} value={String(lab.id)}>
                      {lab.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tipo_ruleta">Tipo de ruleta</Label>
                <Select
                  value={form.tipo_ruleta}
                  onValueChange={(v) => setForm(prev => ({ ...prev, tipo_ruleta: v as 'slot' | 'circular' }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="slot">Slot Machine (Vertical)</SelectItem>
                    <SelectItem value="circular">Ruleta Circular</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="max_ganadores">Max. ganadores</Label>
                <Input
                  id="max_ganadores"
                  type="number"
                  min={1}
                  value={form.max_ganadores}
                  onChange={(e) => setForm(prev => ({ ...prev, max_ganadores: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.mostrar_filtro_pais}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, mostrar_filtro_pais: checked }))}
                className="data-[state=checked]:bg-teal-500"
              />
              <Label>Mostrar filtro por país</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.activo}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, activo: checked }))}
                className="data-[state=checked]:bg-teal-500"
              />
              <Label>Sorteo activo</Label>
            </div>
          </CardContent>
        </Card>

        {/* Campos Excel */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-base">Configuración Excel</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="campos_excel">Columnas requeridas (separadas por coma)</Label>
              <Input
                id="campos_excel"
                value={camposExcelStr}
                onChange={(e) => setCamposExcelStr(e.target.value)}
                placeholder="nombre, correo, pais"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Columnas que debe tener el Excel para cargar participantes
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Opciones fijas (circular) */}
        {form.tipo_ruleta === 'circular' && (
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="text-base">Opciones de la ruleta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={nuevaOpcion}
                  onChange={(e) => setNuevaOpcion(e.target.value)}
                  placeholder="Nueva opción"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOpcion())}
                />
                <Button type="button" variant="outline" onClick={addOpcion}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(form.opciones_fijas ?? []).map((opcion, i) => (
                  <div key={i} className="flex items-center gap-1 bg-teal-500/10 text-teal-700 dark:text-teal-300 px-3 py-1 rounded-full text-sm">
                    {opcion}
                    <button type="button" onClick={() => removeOpcion(i)} className="hover:text-red-500">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de premios */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Lista de premios</CardTitle>
              {(form.mensajes_premios ?? []).length > 0 && (
                <Button type="button" size="sm" variant="ghost" onClick={clearPremios} className="text-red-500 hover:text-red-700">
                  Limpiar todo
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Formulario agregar */}
            <div className="space-y-3">
              {/* Nombres del premio + botón agregar más */}
              <div>
                <Label className="text-xs">Nombre del premio *</Label>
                <div className="space-y-2">
                  {premioNombres.map((nombre, i) => (
                    <div key={i} className="flex gap-2">
                      <Textarea
                        value={nombre}
                        onChange={(e) => updateNombre(i, e.target.value)}
                        placeholder={i === 0 ? 'Ej: Tablet Samsung\nDescripción del premio' : 'Otro premio...'}
                        rows={2}
                        className="resize-none"
                      />
                      {i === premioNombres.length - 1 ? (
                        <Button type="button" variant="outline" size="icon" onClick={addNombreField} className="shrink-0 self-start mt-1">
                          <Plus className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeNombreField(i)} className="shrink-0 text-red-400 hover:text-red-600 self-start mt-1">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Para el ganador #</Label>
                  <Input
                    type="number"
                    min={1}
                    value={premioGanador}
                    onChange={(e) => setPremioGanador(e.target.value)}
                    placeholder="Auto"
                  />
                </div>
                <div>
                  <Label className="text-xs">Mensaje para el ganador</Label>
                  <Textarea
                    value={premioMensaje}
                    onChange={(e) => setPremioMensaje(e.target.value)}
                    placeholder="Felicidades, ganaste..."
                    rows={2}
                    className="resize-none"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">URL de imagen (opcional)</Label>
                <Input
                  value={premioImagen}
                  onChange={(e) => setPremioImagen(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addPremio}
                disabled={!premioNombres.some(n => n.trim())}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar premio
              </Button>
            </div>

            {/* Lista de premios agregados */}
            {(form.mensajes_premios ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No hay premios configurados
              </p>
            ) : (
              <div className="space-y-1">
                {(form.mensajes_premios ?? []).map((premio, realIndex) => {
                  const isEditing = editingPremioIndex === realIndex
                  return isEditing ? (
                    <div key={realIndex} className="space-y-2 p-3 rounded-lg border border-teal-500/50 bg-teal-500/5">
                      <div className="flex gap-2">
                        <Textarea
                          value={editingPremio.mensaje}
                          onChange={(e) => setEditingPremio(prev => ({ ...prev, mensaje: e.target.value }))}
                          placeholder="Nombre del premio"
                          className="flex-1 resize-none"
                          rows={2}
                          autoFocus
                        />
                        <Input
                          type="number"
                          min={1}
                          value={editingPremio.posicion}
                          onChange={(e) => setEditingPremio(prev => ({ ...prev, posicion: parseInt(e.target.value) || 1 }))}
                          className="w-20 self-start"
                          placeholder="#"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Textarea
                          value={editingPremio.mensaje_ganador ?? ''}
                          onChange={(e) => setEditingPremio(prev => ({ ...prev, mensaje_ganador: e.target.value || undefined }))}
                          placeholder="Mensaje para el ganador"
                          rows={2}
                          className="resize-none"
                        />
                        <Input
                          value={editingPremio.imagen_url ?? ''}
                          onChange={(e) => setEditingPremio(prev => ({ ...prev, imagen_url: e.target.value || undefined }))}
                          placeholder="URL de imagen"
                        />
                      </div>
                      <div className="flex justify-end gap-1">
                        <Button type="button" size="sm" variant="ghost" onClick={cancelEditPremio}>
                          <X className="h-3.5 w-3.5 mr-1" />
                          Cancelar
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="bg-teal-800 hover:bg-teal-900 text-white"
                          onClick={saveEditPremio}
                          disabled={!editingPremio.mensaje.trim()}
                        >
                          <Check className="h-3.5 w-3.5 mr-1" />
                          Guardar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div key={realIndex} className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 group">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-teal-500/10 text-teal-700 dark:text-teal-300 font-bold text-xs shrink-0">
                        {premio.posicion}
                      </span>
                      <span className="flex-1 text-sm font-medium whitespace-pre-line">{premio.mensaje}</span>
                      {premio.mensaje_ganador && (
                        <span className="text-xs text-muted-foreground italic truncate max-w-[150px]">{premio.mensaje_ganador}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => startEditPremio(realIndex)}
                        className="text-gray-400 hover:text-teal-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removePremio(realIndex)}
                        className="text-red-400 hover:text-red-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Premios por cantidad (solo en modo edición) */}
        {isEditing && id && <PremiosFormCard sorteoId={id} />}

        {/* Actions */}
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/sorteos')}>
            Cancelar
          </Button>
          <Button
            type="submit"
            className="bg-teal-800 hover:bg-teal-900 text-white"
            disabled={crear.isPending || actualizar.isPending || !form.nombre}
          >
            <Save className="h-4 w-4 mr-2" />
            {crear.isPending || actualizar.isPending ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear Sorteo'}
          </Button>
        </div>
      </form>
    </div>
  )
}
